import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  useCreateCspRequest, useCspCompanies, useCspMe, useCspProducts,
  type CspProduct, type CspRequest, type CspRequestType,
} from '@/api/csp';
import { STATUS_LABEL, money, useCspPrefs, type CspStatus } from '@/lib/csp';
import { cn } from '@/lib/utils';
import { FloatingPanel } from '@/components/FloatingPanel';

// -------- Badges --------

const STATUS_CLASS: Record<CspStatus, string> = {
  active: 'bg-success-soft text-success',
  expiring: 'bg-warning-soft text-warning',
  expired: 'bg-destructive/10 text-destructive',
  unknown: 'bg-muted text-muted-foreground',
};

export function StatusBadge({ status, label }: { status: CspStatus; label?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold', STATUS_CLASS[status])}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label ?? STATUS_LABEL[status]}
    </span>
  );
}

export function RenewFlag({ on }: { on: boolean }) {
  return <span className={cn('text-xs font-medium', on ? 'text-success' : 'text-destructive')}>{on ? 'On' : 'Off'}</span>;
}

export function Pill({ tone, children }: { tone: 'good' | 'warn' | 'bad' | 'muted'; children: React.ReactNode }) {
  const cls = { good: 'bg-success-soft text-success', warn: 'bg-warning-soft text-warning', bad: 'bg-destructive/10 text-destructive', muted: 'bg-muted text-muted-foreground' }[tone];
  return <span className={cn('inline-flex items-center whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold capitalize', cls)}>{children}</span>;
}

/** The "Hide long-expired" switch. Shared state across every CSP page. */
export function LongExpiredToggle() {
  const [prefs, setPrefs] = useCspPrefs();
  return (
    <label className="inline-flex cursor-pointer select-none items-center gap-1.5 text-xs text-muted-foreground"
      title={`Subscriptions expired more than ${prefs.longExpiredDays} days ago are past Microsoft's recovery window`}>
      <input type="checkbox" className="h-3.5 w-3.5 accent-primary" checked={prefs.hideLongExpired}
        onChange={(e) => setPrefs({ hideLongExpired: e.target.checked })} />
      Hide long-expired
    </label>
  );
}

// -------- Searchable picker --------

export interface PickOption { value: string; label: string; hint?: string }

/**
 * Type-to-filter picker over a known option list (Autotask clients, CSV customers…).
 * `allowFree` lets the typed text stand as the value when nothing is picked.
 */
export function SearchSelect({
  options, value, onChange, placeholder, allOption, allowFree, loading, className,
}: {
  options: PickOption[];
  value: string;
  onChange: (value: string, option: PickOption | null) => void;
  placeholder?: string;
  /** Label for an explicit "no filter" first row (value ''). */
  allOption?: string;
  allowFree?: boolean;
  loading?: boolean;
  className?: string;
}) {
  const selected = options.find((o) => o.value === value) ?? null;
  const [text, setText] = useState(selected?.label ?? value);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setText(selected?.label ?? value); }, [selected?.label, value]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current && !ref.current.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const matches = useMemo(() => {
    const q = text.trim().toLowerCase();
    const list = q && q !== (selected?.label ?? '').toLowerCase() ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
    return list.slice(0, 60);
  }, [options, text, selected?.label]);

  const pick = (o: PickOption | null) => {
    onChange(o?.value ?? '', o);
    setText(o?.label ?? '');
    setOpen(false);
  };

  return (
    <div ref={ref} className={cn('relative', className)}>
      <Input
        value={text}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          if (allowFree) onChange(e.target.value, null);
        }}
        className="h-8 pr-7 text-sm"
      />
      <ChevronDown className="pointer-events-none absolute right-2 top-2 h-4 w-4 text-muted-foreground" />
      {open && (
        <FloatingPanel ref={panelRef} anchor={ref.current} className="text-sm">
          {allOption && (
            <button type="button" className="block w-full px-3 py-1.5 text-left italic text-muted-foreground hover:bg-muted" onClick={() => pick(null)}>{allOption}</button>
          )}
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>
          ) : matches.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">No matches</div>
          ) : matches.map((o) => (
            <button key={o.value} type="button" onClick={() => pick(o)}
              className={cn('block w-full truncate px-3 py-1.5 text-left hover:bg-muted', o.value === value && 'bg-muted font-medium')}>
              {o.label}{o.hint && <span className="ml-1.5 text-xs text-muted-foreground">{o.hint}</span>}
            </button>
          ))}
        </FloatingPanel>
      )}
    </div>
  );
}

// -------- Workflow result --------

/** Toast the order-workflow outcome (Synnex stub + Autotask contract plan/change). */
export function announceRequest(r: CspRequest) {
  if (!r.workflow) {
    toast.success('Request submitted for approval');
    return;
  }
  const at = r.workflow.steps.find((s) => s.system === 'autotask');
  const msg = r.workflow.writesEnabled ? 'Approved — order workflow ran' : 'Approved — Autotask writes are off, so this is the plan';
  (at?.status === 'error' || at?.status === 'needs_selection' ? toast.warning : toast.success)(msg, {
    description: r.workflow.steps.map((s) => `${s.system === 'autotask' ? 'Autotask' : 'Synnex'}: ${s.detail}`).join('\n'),
    duration: 12_000,
  });
}

// -------- New request / purchase dialog --------

/**
 * Raise a licence request — or, for approvers, purchase straight away (auto-approved,
 * order workflow fires). Opened from Products (product fixed) and Requests (pick one).
 */
export function RequestDialog({ open, onOpenChange, product: fixedProduct }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product?: CspProduct | null;
}) {
  const me = useCspMe();
  const products = useCspProducts();
  const companies = useCspCompanies(open);
  const create = useCreateCspRequest();
  const [serviceId, setServiceId] = useState<string>('');
  const [client, setClient] = useState<{ id: number | null; name: string }>({ id: null, name: '' });
  const [type, setType] = useState<CspRequestType>('additional');
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setServiceId(fixedProduct ? String(fixedProduct.atServiceId) : '');
    setClient({ id: null, name: '' });
    setType('additional');
    setQty(1);
    setNotes('');
  }, [open, fixedProduct]);

  const approver = !!me.data?.isApprover;
  const product = fixedProduct ?? products.data?.products.find((p) => String(p.atServiceId) === serviceId) ?? null;
  const clientOptions = useMemo(() => (companies.data ?? []).map((c) => ({ value: String(c.id), label: c.name })), [companies.data]);
  const valid = !!product && !!client.name.trim() && qty >= 1;

  async function submit() {
    if (!product || !valid) return;
    try {
      const r = await create.mutateAsync({
        customer: client.name.trim(), atCompanyId: client.id, product: product.name, term: product.term ?? '',
        atServiceId: product.atServiceId, quantity: qty, type, notes: notes.trim(), autoApprove: approver,
      });
      onOpenChange(false);
      announceRequest(r);
    } catch (e) {
      toast.error(`Could not submit: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{approver ? 'Purchase for client' : 'Request licences'}</DialogTitle>
          <p className="text-xs text-muted-foreground">
            {approver ? 'You are an approver — this is approved immediately and the order workflow runs.' : 'Routed to an approver before anything is ordered.'}
          </p>
        </DialogHeader>
        <div className="space-y-3 px-6 pb-2">
          {fixedProduct ? (
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
              <div className="text-sm font-medium text-foreground">{fixedProduct.name}</div>
              <div className="text-xs text-muted-foreground">{fixedProduct.sku} · {fixedProduct.term ?? '—'} · sell {money(fixedProduct.atSell)} ex GST</div>
            </div>
          ) : (
            <Field label="Product (MSNCE service)">
              <select value={serviceId} onChange={(e) => setServiceId(e.target.value)}
                className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground">
                <option value="">{products.isLoading ? 'Loading products…' : 'Choose a product…'}</option>
                {products.data?.products.map((p) => (
                  <option key={p.atServiceId} value={p.atServiceId}>{p.name}{p.term ? ` (${p.term})` : ''}</option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Client (Autotask)">
            <SearchSelect options={clientOptions} value={client.id != null ? String(client.id) : client.name} allowFree loading={companies.isLoading}
              placeholder="Search Autotask clients…"
              onChange={(v, o) => setClient(o ? { id: Number(o.value), name: o.label } : { id: null, name: v })} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select value={type} onChange={(e) => setType(e.target.value as CspRequestType)}
                className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground">
                <option value="additional">Additional (existing line)</option>
                <option value="new">New licence (new line)</option>
              </select>
            </Field>
            <Field label="Quantity">
              <QtyStepper value={qty} min={1} onChange={setQty} />
            </Field>
          </div>
          <Field label="Notes / justification">
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Why is this needed?" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-6 py-3">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={!valid || create.isPending}>
            {create.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {approver ? 'Purchase now' : 'Submit for approval'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

export function QtyStepper({ value, onChange, min = 0, max = 99_999, size = 'md', minusDisabled, minusTitle }: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  size?: 'sm' | 'md';
  minusDisabled?: boolean;
  minusTitle?: string;
}) {
  const clamp = (n: number) => Math.max(min, Math.min(max, n));
  const btn = cn('flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40',
    size === 'sm' ? 'h-7 w-6' : 'h-8 w-8');
  return (
    <div className="inline-flex items-center overflow-hidden rounded-md border border-input bg-background">
      <button type="button" className={btn} disabled={minusDisabled || value <= min} title={minusTitle} onClick={() => onChange(clamp(value - 1))}>−</button>
      <input type="number" value={value} min={min} max={max}
        onChange={(e) => onChange(clamp(parseInt(e.target.value, 10) || 0))}
        className={cn('border-x border-input bg-transparent text-center text-sm tabular-nums text-foreground outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none',
          size === 'sm' ? 'h-7 w-12' : 'h-8 w-16')} />
      <button type="button" className={btn} disabled={value >= max} title="Add seats" onClick={() => onChange(clamp(value + 1))}>+</button>
    </div>
  );
}
